import { PDFDownloadLink } from '@react-pdf/renderer';
import React, { CSSProperties, useEffect, useState } from 'react'
import AnalyzeDataPDF from '../PDF/AnalyzeDataPDF';
import Panel from './Panel';
import ViolationsPDF from '../PDF/ViolationsPDF';
import { riskLevel } from './AnalyseCalc';

export interface IProbability {
    prob: number;
    text: string;
}

export interface Question {
    value: boolean;
    justification: string;
    risk?: RiskJustification;
}

export interface RiskJustification {
    value: number;
    text?: string;
    justification?: string;
}

export interface AnalyzeData {
    infringementDate: string;
    admin: string;
    infringement: string;
    question2: Question[],
    question3: Question[],
    identityProbability: RiskJustification;
    confidentialLoss: RiskJustification;
    integrityLoss: RiskJustification;
    availabilityLoss: RiskJustification;
    intentionalAction: RiskJustification;
    riskProb: number;
    additionalRisks: number[];
}

export interface TotalRisk {
    value: number;
    name: string;
    style: string;
    details: string;
    reportObligation: string;
    progressBarValue: number;
}

export interface PDFData extends AnalyzeData {
    riskTotal: TotalRisk
}

export default function Violations() {

    const [riskTotal, setRiskToal] = useState<TotalRisk>({
        value: 0,
        style: "bg-success",
        name: 'Niska',
        details: 'Brak istotnego naruszenia lub nieznaczne niedogodności wywołane naruszeniem',
        reportObligation: 'Nie ma obowiązku zgłoszenia naruszenia',
        progressBarValue: 20
    });
    const [data, setData] = useState<AnalyzeData>({
        infringementDate: '',
        admin: '',
        infringement: '',
        question2: [
            { value: false, justification: '', risk: { value: 1 }},
            { value: false, justification: '', risk: { value: 2 }},
            { value: false, justification: '', risk: { value: 3 }},
            { value: false, justification: '', risk: { value: 4 }},
        ],
        question3: [
            { value: false, justification: '' },
            { value: false, justification: '' },
            { value: false, justification: '' },
            { value: false, justification: '' },
            { value: false, justification: '' },
            { value: false, justification: '' },
            { value: false, justification: '' },
            { value: false, justification: '' },
        ],
        identityProbability: { value: 0 },
        availabilityLoss: { value: 0 },
        confidentialLoss: { value: 0 },
        integrityLoss: { value: 0 },
        intentionalAction: { value: 0 },
        riskProb: 1,
        additionalRisks: [0, 0, 0, 0]
    })

    useEffect(() => {

        const question2RiskSum = data
            .question2
            .filter(x => x.value)
            .map(x => x.risk!.value)
            .reduce((x, y) => x + y, 0);

        const question3RiskSum = data
            .question3
            .filter(x => x.value)
            .length;

        const q2Val = Math.min(4, question2RiskSum);
        const q3Val = Math.min(4, question3RiskSum);

        let total = (q2Val + q3Val) * data.riskProb;

        if(total > 0) {
            data.additionalRisks.forEach(x => total += x);
        }

        if(total < 2) {
            setRiskToal({
                ...riskTotal,
                progressBarValue: 25,
                value: total,
                style: "bg-success",
                name: "Niska",
                details: "brak istotnego naruszenia lub nieznaczne niedogodności wywołane naruszeniem",
                reportObligation: 'Nie ma obowiązku zgłoszenia naruszenia'
            });
        }
        else if(total < 3) {
            setRiskToal({
                ...riskTotal,
                progressBarValue: 50,
                value: total,
                style: "bg-info",
                name: "Średnia",
                details: "średniej wagi naruszenie, niedogodności wywołane naruszeniem są możliwe do usunięcia",
                reportObligation: 'Występuje obowiązek zgłoszenia naruszenia organowi nadzorczemu'
            });
        }
        else if(total < 4) {
            setRiskToal({
                ...riskTotal,
                progressBarValue: 75,
                value: total,
                style: "bg-warning",
                name: "Wysoka",
                details: "dużej wagi naruszenie, niedogodności wywołane naruszeniem są możliwe do usunięcia, ale z poważnymi konsekwencjami",
                reportObligation: 'Występuje obowiązek zgłoszenia naruszenia organowi nadzorczemu i osobom, których naruszenie dotyczy'
            });
        }
        else {
            setRiskToal({
                ...riskTotal,
                progressBarValue: 100,
                value: total,
                style: "bg-danger",
                name: "Bardzo wysoka",
                details: "bardzo dużej wagi naruszenie, ryzyko wystąpienia poważnych, w tym nieodwracalnych, konsekwencji",
                reportObligation: 'Występuje obowiązek zgłoszenia naruszenia organowi nadzorczemu i osobom, których naruszenie dotyczy'
            });
        }

    }, [data])

    const riskProgressStyle = (): CSSProperties => {
        const percent = riskTotal.value * 10;
        return {width: percent + "%"}
    }

    const changeData = (field: string, e: any) => {
        setData({
            ...data,
            [field]: e.target.value
        })
    }

    const question2Marked = (id: number, e: any) => {
        setData({
            ...data,
            question2: data.question2.map((x, i) => {
                if(i === id) return {
                    ...x,
                    value: e.target.checked
                }

                return x;
            })
        })
    }

    const question2RiskChange = (id: number, riskValue: number) => {
        setData({
            ...data,
            question2: data.question2.map((x, i) => {
                if(i === id) return {
                    ...x,
                    risk: {
                        ...x.risk,
                        value: riskValue
                    }
                }

                return x;
            })
        })
    }

    const question2RiskJustificationChange = (id: number, e: any) => {
        setData({
            ...data,
            question2: data.question2.map((x, i) => {
                if(i === id) return {
                    ...x,
                    justification: e.target.value
                }

                return x;
            })
        })
    }

    const question3Marked = (id: number, e: any) => {
        setData({
            ...data,
            question3: data.question3.map((x, i) => {
                if(i === id) return {
                    ...x,
                    value: e.target.checked
                }

                return x;
            })
        })
    }

    const question3Justification = (id: number, e: any) => {
        setData({
            ...data,
            question3: data.question3.map((x, i) => {
                if(i === id) return {
                    ...x,
                    justification: e.target.value
                }

                return x;
            })
        })
    }

    const changeIdentityProb = (_: string, e: any) => {

        const probs: {[id: string]: IProbability} = {
            "1": { prob: 0.25, text: "Niskie" },
            "2": { prob: 0.5, text: "Średnie" },
            "3": { prob: 0.75, text: "Wysokie" },
            "4": { prob: 1, text: "Bardzo wysokie" },
        }

        const probability = probs[e.target.value];

        setData({
            ...data,
            identityProbability: {
                ...data.identityProbability,
                value: e.target.value,
                text: probability.text
            },
            riskProb: probability.prob
        })
    }

    const changeIdentityText = (_: string, e: any) => {

        setData({
            ...data,
            identityProbability: {
                ...data.identityProbability,
                text: e.target.value
            }
        })
    }

    const changeConfidentialLossRisk = (field: string, e: any) => {

        const probs: {[id: string]: IProbability} = {
            "1": { prob: 0.25, text: "Ujawniono dane znanym odbiorcom" },
            "2": { prob: 0.5, text: "Ujawniono dane nieznanej liczbie odbiorców" },
            "3": { prob: 0, text: "Nie dotyczy" }
        }

        const prob = probs[e.target.value];

        setData({
            ...data,
            confidentialLoss: {
                ...data.confidentialLoss,
                text: prob.text,
                value: e.target.value
            },
            additionalRisks: changeAdditionalRisk(0, prob.prob)
        })
    }

    const changeConfidentialLossText = (field: string, e: any) => {
        setData({
            ...data,
            confidentialLoss: {
                ...data.confidentialLoss,
                [field]: e.target.value
            }
        })
    }

    const changeAvailabilityLossRisk = (field: string, e: any) => {

        const probs: {[id: string]: IProbability} = {
            "1": { prob: 0.25, text: "Tymczasowa niedostępność danych" },
            "2": { prob: 0.5, text: "Stała niedostępność danych i nie ma możliwości ich odzyskania" },
            "3": { prob: 0, text: "Nie dotyczy" }
        }

        const prob = probs[e.target.value];

        setData({
            ...data,
            availabilityLoss: {
                ...data.availabilityLoss,
                text: prob.text,
                value: e.target.value
            },
            additionalRisks: changeAdditionalRisk(2, prob.prob)
        })
    }

    const changeAdditionalRisk = (id: number, value: number) => {
        return data.additionalRisks.map((x, i) => {if(i !== id) return x; else return value})
    }

    const changeAvailabilityLossText = (field: string, e: any) => {

        setData({
            ...data,
            availabilityLoss: {
                ...data.availabilityLoss,
                [field]: e.target.value
            }
        })
    }

    const changeIntegrityLossRisk = (field: string, e: any) => {

        const probs: {[id: string]: IProbability} = {
            "1": { prob: 0.25, text: "Wciąż jest możliwość skorygowania zmienionych danych" },
            "2": { prob: 0.5, text: "Nie ma możliwości skorygowania zmienionych danych" },
            "3": { prob: 0, text: "Nie dotyczy" }
        }

        const prob = probs[e.target.value];

        setData({
            ...data,
            integrityLoss: {
                ...data.integrityLoss,
                text: prob.text,
                value: e.target.value
            },
            additionalRisks: changeAdditionalRisk(1, prob.prob)
        })
    }

    const changeIntegrityLossText = (field: string, e: any) => {
        setData({
            ...data,
            integrityLoss: {
                ...data.integrityLoss,
                [field]: e.target.value
            }
        })
    }

    const changeIntentionalActionRisk = (field: string, e: any) => {
        const probs: {[id: string]: IProbability} = {
            "1": { prob: 0.5, text: "Naruszenie było celowe" },
            "2": { prob: 0, text: "Naruszenie było przypadkowe" }
        }

        const prob = probs[e.target.value];

        setData({
            ...data,
            intentionalAction: {
                ...data.intentionalAction,
                text: prob.text,
                value: e.target.value
            },
            additionalRisks: changeAdditionalRisk(3, prob.prob)
        })
    }

    const changeIntentionalActionText = (field: string, e: any) => {
        setData({
            ...data,
            intentionalAction: {
                ...data.intentionalAction,
                [field]: e.target.value
            }
        })
    }

  const document = () => {
    return <ViolationsPDF pdf={data} riskTotal={riskTotal} />
  }

  return (
    <div className='row'>
        <div className='col-lg-10 col-sm-12'>
            <div>
                <div className="row">
                    <div className="col text-center">
                        <h3 className="text-dark-gray fw-700 ls-minus-1px">Oceń, czy jest wymóg zgłoszenia naruszenia</h3>
                    </div>
                </div>
                <div className="row">
                    <Panel header='1. Opis naruszenia'>
                        <div>
                            <label className="text-medium-gray mb-10px">Data naruszenia</label>
                            <textarea className="form-control" onChange={e => changeData("infringementDate", e)} value={data.infringementDate}></textarea>
                        </div>
                        <div>
                            <label className="text-medium-gray mb-25px mt-2 color-info">Nazwa administratora danych, którego dotyczyło naruszenie</label>
                            <textarea className="form-control" onChange={e => changeData("admin", e)} value={data.admin}></textarea>
                        </div>
                        <div>
                            <label className="text-medium-gray mb-25px mt-2 color-info">Opis na czym polegało naruszenie</label>
                            <textarea className="form-control" onChange={e => changeData("infringement", e)} value={data.infringement}></textarea>
                        </div>
                    </Panel>
                    
                </div>
                <div className='row'>
                    <Panel header='2. Zakres ujawnionych danych'>
                        <div className='form-check mt-4'>
                            <label className="text-medium-gray mb-10px checkbox"><input className="form-check-input" type="checkbox" onChange={e => question2Marked(0, e)} checked={data.question2[0].value} id="flexCheckDefault" />Czy zostały ujawnione dane zwykłe</label>
                            <div hidden={!data.question2[0].value}>
                                <div className='row mt-2'>
                                    <div className='col-lg-3'>
                                        <div className="btn-group" role="group" aria-label="Basic radio toggle button group">
                                            <input type="radio" className="btn-check" name="btnradio0" checked={data.question2[0].risk!.value === 1} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(0, 1)}>1</label>
                                            <input type="radio" className="btn-check" name="btnradio0" checked={data.question2[0].risk!.value === 2} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(0, 2)}>2</label>
                                            <input type="radio" className="btn-check" name="btnradio0" checked={data.question2[0].risk!.value === 3} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(0, 3)}>3</label>
                                            <input type="radio" className="btn-check" name="btnradio0" checked={data.question2[0].risk!.value === 4} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(0, 4)} >4</label>
                                        </div>
                                    </div>
                                    <div className='col-lg-9'>
                                        <input className="form-control" type="text" onChange={e => question2RiskJustificationChange(0, e)} value={data.question2[0].justification} id="flexCheckDefault" placeholder="Uzasadnienie (opcja)" />
                                    </div>
                                </div>
                                <div className="row text-medium-gray font-14">
                                    <p>
                                        1 - Domyślna opcja. Wybierz tę kategorię, jeśli naruszenie obejmowało: podstawowe dane osobowe (imię i nazwisko), adresy do korespondencji, dane kontaktowe (telefon, e-mail), informacje o wykształceniu lub doświadczeniu zawodowym.
                                    </p>
                                    <p>
                                        2 - Wybierz tę kategorię, jeśli skala wycieku podstawowych danych osobowych jest znacząca, charakter firmy/organizacji, która doświadczyła naruszenia, jest szczególny i gdy te czynniki mogą prowadzić do profilowania osoby, której dane ujawniono.
                                    </p>
                                    <p>
                                        3 - Wybierz tę kategorię, gdy wyciek danych lub specyfika firmy/organizacji, która doświadczyła naruszenia, może doprowadzić do profilowania osoby, której dane ujawniono, w zakresie stanu zdrowia, orientacji seksualnej, przekonań politycznych lub religijnych.
                                    </p>
                                    <p>
                                        4 - Wybierz tę kategorię, gdy wyciek danych może stanowić poważne zagrożenie dla bezpieczeństwa lub dobrostanu osób, których dane dotyczą, szczególnie w przypadku osób wymagających szczególnej opieki (dzieci, osoby starsze, osoby niepełnosprawne), osób w trudnej sytuacji życiowej lub osób narażonych na przemoc lub prześladowanie.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className='form-check'>
                            <label className="text-medium-gray mb-10px checkbox"><input className="form-check-input" type="checkbox" onChange={e => question2Marked(1, e)} checked={data.question2[1].value} id="flexCheckDefault" />Czy zostały ujawnione dane dotyczące zachowań</label>
                            <div hidden={!data.question2[1].value}>
                                <div className='row mt-2'>
                                    <div className='col-lg-3'>
                                        <div className="btn-group" role="group" aria-label="Basic radio toggle button group">
                                            <input type="radio" className="btn-check" name="btnradio1" checked={data.question2[1].risk!.value === 1} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(1, 1)}>1</label>
                                            <input type="radio" className="btn-check" name="btnradio1" checked={data.question2[1].risk!.value === 2} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(1, 2)}>2</label>
                                            <input type="radio" className="btn-check" name="btnradio1" checked={data.question2[1].risk!.value === 3} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(1, 3)}>3</label>
                                            <input type="radio" className="btn-check" name="btnradio1" checked={data.question2[1].risk!.value === 4} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(1, 4)} >4</label>
                                        </div>
                                    </div>
                                    <div className='col-lg-9'>
                                    <input className="form-control" type="text" onChange={e => question2RiskJustificationChange(1, e)} value={data.question2[1].justification} id="flexCheckDefault" placeholder="Uzasadnienie - opcjonalnie" />
                                    </div>
                                </div>
                                <div className='row text-medium-gray font-14'>
                                    <p>
                                        1 - Wybierz tę kategorię, gdy wyciek danych nie ujawnia istotnych informacji o zachowaniach lub preferencjach osoby lub gdy informacje te są łatwo dostępne publicznie (np. w Internecie).
                                    </p>
                                    <p>
                                        2 - Domyślna opcja. Wybierz tę kategorię, gdy naruszenie obejmuje dane dotyczące lokalizacji i przemieszczania się, preferencji osobistych i upodobań.
                                    </p>
                                    <p>
                                        3 - Wybierz tę kategorię, gdy wyciek danych umożliwia stworzenie szczegółowego profilu osoby, obejmującego szczegółowe informacje o jej życiu codziennym i o nawykach.
                                    </p>
                                    <p>
                                        4 - Wybierz tę kategorię, gdy wyciek danych behawioralnych umożliwia stworzenie profilu osoby, który może ujawnić informacje z kategorii szczególnie chronionych, takie jak stan zdrowia, orientacja seksualna, przekonania polityczne lub religijne, pochodzenie rasowe lub etniczne, przynależność do związków zawodowych, dane genetyczne lub biometryczne itp.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className='form-check'>
                            <label className="text-medium-gray mb-10px checkbox"><input className="form-check-input" type="checkbox" onChange={e => question2Marked(2, e)} checked={data.question2[2].value} id="flexCheckDefault" />Czy zostały ujawnione dane finansowe</label>
                            <div hidden={!data.question2[2].value}>
                                <div className='row mt-2'>
                                    <div className='col-lg-3'>
                                        <div className="btn-group" role="group" aria-label="Basic radio toggle button group">
                                            <input type="radio" className="btn-check" name="btnradio2" checked={data.question2[2].risk!.value === 1} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(2, 1)}>1</label>
                                            <input type="radio" className="btn-check" name="btnradio2" checked={data.question2[2].risk!.value === 2} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(2, 2)}>2</label>
                                            <input type="radio" className="btn-check" name="btnradio2" checked={data.question2[2].risk!.value === 3} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(2, 3)}>3</label>
                                            <input type="radio" className="btn-check" name="btnradio2" checked={data.question2[2].risk!.value === 4} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(2, 4)} >4</label>
                                        </div>
                                    </div>
                                    <div className='col-lg-9'>
                                    <input className="form-control" type="text" onChange={e => question2RiskJustificationChange(2, e)} value={data.question2[2].justification} id="flexCheckDefault" placeholder="Uzasadnienie (opcja)" />
                                    </div>
                                </div>
                                <div className='row text-medium-gray font-14'>
                                    <p>
                                        1 - Wybierz tę kategorię, jeśli ujawnione dane nie pozwalają na uzyskanie istotnych informacji o sytuacji finansowej osoby.
                                    </p>
                                    <p>
                                        2 - Wybierz tę kategorię, jeśli ujawnione dane obejmują pewne informacje finansowe, ale nie pozwalają na pełną ocenę sytuacji finansowej osoby (np. numer rachunku bankowego bez szczegółów konta i jego historii.
                                    </p>
                                    <p>
                                        3 - Domyślna opcja. Wybierz tę kategorię, gdy naruszenie obejmuje szczegółowe informacje finansowe osoby, takie jak dochody i ich źródła, historia płatności, szczegóły przelewów, wyciągi z kont, salda rachunków, portfel inwestycyjny, świadczenia z opieki społecznej, pomoc materialna.
                                    </p>
                                    <p>
                                        4 - Wybierz tę kategorię, gdy wyciek danych stwarza wysokie ryzyko nadużyć finansowych lub umożliwia stworzenie szczegółowego profilu społeczno-finansowego osoby, np. numery kart kredytowych oraz ich kodów zabezpieczających, loginy i hasła do danych bankowych.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className='form-check'>
                            <label className="text-medium-gray mb-10px checkbox"><input className="form-check-input" type="checkbox" onChange={e => question2Marked(3, e)} checked={data.question2[3].value} id="flexCheckDefault" />Czy zostały ujawnione dane szczególnej kategorii</label>
                            <div hidden={!data.question2[3].value}>
                                <div className='row mt-2'>
                                    <div className='col-lg-3'>
                                        <div className="btn-group" role="group" aria-label="Basic radio toggle button group">
                                            <input type="radio" className="btn-check" name="btnradio3" checked={data.question2[3].risk!.value === 1} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(3, 1)}>1</label>
                                            <input type="radio" className="btn-check" name="btnradio3" checked={data.question2[3].risk!.value === 2} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(3, 2)}>2</label>
                                            <input type="radio" className="btn-check" name="btnradio3" checked={data.question2[3].risk!.value === 3} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(3, 3)}>3</label>
                                            <input type="radio" className="btn-check" name="btnradio3" checked={data.question2[3].risk!.value === 4} readOnly />
                                            <label className="btn btn-outline-primary" onClick={() => question2RiskChange(3, 4)} >4</label>
                                        </div>
                                    </div>
                                    <div className='col-lg-9'>
                                    <input className="form-control" type="text" onChange={e => question2RiskJustificationChange(3, e)} value={data.question2[3].justification} id="flexCheckDefault" placeholder="Uzasadnienie (opcja)" />
                                    </div>
                                </div>
                                <div className='row text-medium-gray font-14'>
                                    <p>
                                        1 - Wybierz tę kategorię, jeśli ujawnione dane nie pokazują żadnych istotnych informacji szczególnej kategorii o osobie lub też ujawnione dane są łatwo dostępne publicznie.
                                    </p>
                                    <p>
                                        2 - Wybierz tę kategorię, jeśli na podstawie ujawnionych danych można dokonać ogólnych założeń na temat danej osoby, np. na temat jej stanu zdrowia.
                                    </p>
                                    <p>
                                        3 - Wybierz tę kategorię, gdy wyciek danych lub specyfika firmy/organizacji, która go doświadczyła, może prowadzić do wyciągania wniosków dotyczących informacji z kategorii danych szczególnych.
                                    </p>
                                    <p>
                                        4 - Domyślna opcja. Wybierz tę kategorię, gdy naruszenie obejmuje informacje ujawniające dane szczególnych kategorii, np. pochodzenie rasowe lub etniczne, przekonania polityczne lub religijne, przynależność do związków zawodowych, dane genetyczne lub biometryczne, stan zdrowia, orientacja seksualnej.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Panel>
                </div>
                <div className='row'>
                    <Panel header='3. Czynniki wpływające na poziom ryzyka'>
                        <div>
                            <div className='mb-2 mt-4 pb-2 border-bottom flex-lg'>
                                <div className='form-check col'>
                                    <label className="text-medium-gray mb-10px checkbox"><input className="form-check-input" type="checkbox" onChange={e => question3Marked(0, e)} checked={data.question3[0].value} id="flexCheckDefault" />Duża liczba osób, których dane zostały ujawnione</label>
                                </div>
                                <div className='col-lg-4'>
                                    <input className="form-control" type="text" placeholder="Opis (opcja)" onChange={e => question3Justification(0, e)} /> 
                                </div>
                            </div>

                            <div className='mb-2 pb-2 border-bottom flex-lg'>
                                <div className='form-check col'>
                                    <label className="text-medium-gray mb-10px checkbox"><input className="form-check-input" type="checkbox" onChange={e => question3Marked(1, e)} checked={data.question3[1].value} value="" id="flexCheckDefault" />Duży zakres danych na temat poszczególnych osób</label>
                                </div>
                                <div className='col-lg-4'>
                                    <input className="form-control" type="text" placeholder="Opis (opcja)" onChange={e => question3Justification(1, e)} /> 
                                </div>
                            </div>

                            <div className='mb-2 pb-2 border-bottom flex-lg'>
                                <div className='form-check col'>
                                    <label className="text-medium-gray mb-10px checkbox"><input className="form-check-input" type="checkbox" onChange={e => question3Marked(2, e)} checked={data.question3[2].value} value="" id="flexCheckDefault" />Specyfika osób, których dane dotyczą</label>
                                </div>
                                <div className='col-lg-4'>
                                    <input className="form-control" type="text" placeholder="Opis (opcja)" onChange={e => question3Justification(2, e)} /> 
                                </div>
                            </div>

                            <div className='mb-2 pb-2 border-bottom flex-lg'>
                                <div className='form-check col'>
                                    <label className="text-medium-gray mb-10px checkbox"><input className="form-check-input" type="checkbox" onChange={e => question3Marked(3, e)} checked={data.question3[3].value} value="" id="flexCheckDefault" />Specyfika administratora danych</label>
                                </div>
                                <div className='col-lg-4'>
                                    <input className="form-control" type="text" placeholder="Opis (opcja)" onChange={e => question3Justification(3, e)} /> 
                                </div>
                            </div>

                            <div className='mb-2 pb-2 border-bottom flex-lg'>
                                <div className='form-check col'>
                                    <label className="text-medium-gray mb-10px checkbox"><input className="form-check-input" type="checkbox" onChange={e => question3Marked(4, e)} checked={data.question3[4].value} value="" id="flexCheckDefault" />Szczególny charakter danych</label>
                                </div>
                                <div className='col-lg-4'>
                                    <input className="form-control" type="text" placeholder="Opis (opcja)" onChange={e => question3Justification(4, e)} /> 
                                </div>
                            </div>

                            <div className='mb-2 pb-2 border-bottom flex-lg'>
                                <div className='form-check col'>
                                    <label className="text-medium-gray mb-10px checkbox"><input className="form-check-input" type="checkbox" onChange={e => question3Marked(5, e)} checked={data.question3[5].value} value="" id="flexCheckDefault" />Publiczna dostępność danych przed ich ujawnieniem</label>
                                </div>
                                <div className='col-lg-4'>
                                    <input className="form-control" type="text" placeholder="Opis (opcja)" onChange={e => question3Justification(5, e)} /> 
                                </div>
                            </div>

                            <div className='mb-2 pb-2 border-bottom flex-lg'>
                                <div className='form-check col'>
                                    <label className="text-medium-gray mb-10px checkbox"><input className="form-check-input" type="checkbox" onChange={e => question3Marked(6, e)} checked={data.question3[6].value} value="" id="flexCheckDefault" />Aktualność i poprawność danych</label>
                                </div>
                                <div className='col-lg-4'>
                                    <input className="form-control" type="text" placeholder="Opis (opcja)" onChange={e => question3Justification(6, e)} /> 
                                </div>
                            </div>

                            <div className='mb-2 pb-2 border-bottom flex-lg'>
                                <div className='form-check col'>
                                    <label className="text-medium-gray mb-10px checkbox"><input className="form-check-input" type="checkbox" onChange={e => question3Marked(7, e)} checked={data.question3[7].value} value="" id="flexCheckDefault" />Zaufani odbiorcy</label>
                                </div>
                                <div className='col-lg-4'>
                                    <input className="form-control" type="text" placeholder="Opis (opcja)" onChange={e => question3Justification(7, e)} /> 
                                </div>
                            </div>
                        </div>
                    </Panel>
                </div>
                <div className='row'>
                    <Panel header='4. Ocena prawdopodobieństwa identyfikacji osób, których dane dotyczą'>
                        <div className='row'>
                            <div className="col-lg-6">
                                <select className="form-select intensity" onChange={e => changeIdentityProb("value", e)} value={data.identityProbability.value ? data.identityProbability.value : "0"}>
                                    <option value="0" disabled>Wybierz odpowiedź</option>
                                    <option value="1" data-val="0.25">Niskie</option>
                                    <option value="2" data-val="0.5">Średnie</option>
                                    <option value="3" data-val="0.75">Wysokie</option>
                                    <option value="4" data-val="1">Bardzo wysokie</option>
                                </select>
                            </div>
                            <div className='col-lg-6 mt-sm-2'>
                                <input className="form-control" type="text" value={data.identityProbability.justification} onChange={e => changeIdentityText("justification", e)} placeholder="Opis (opcja)" />
                            </div>
                        </div>
                    </Panel>
                </div>
                <div className='row'>
                    <Panel header='5. Jaki był charakter naruszenia?'>
                        <div className='row col-12'>
                            <label className='text-medium-gray mb-25px'>Ujawnienie danych i utrata poufności</label>
                        </div>
                        <div className='row'>
                            <div className="col-lg-6">
                                <select className="form-select intensity" onChange={e => changeConfidentialLossRisk("value", e)} value={data.confidentialLoss.value ? data.confidentialLoss.value : "0"}>
                                    <option value="0" disabled>Wybierz odpowiedź</option>
                                    <option value="1">Ujawniono dane znanym odbiorcom</option>
                                    <option value="2">Ujawniono dane nieznanej liczbie odbiorców</option>
                                    <option value="3">Nie dotyczy</option>
                                </select>
                            </div>
                            <div className='col-lg-6 mt-sm-2'>
                                <textarea className="form-control" value={data.confidentialLoss.justification} onChange={e => changeConfidentialLossText("justification", e)} placeholder="Opis (opcja)" />
                            </div>
                        </div>

                        <div className='row col-12 mt-2'>
                            <label className='text-medium-gray mb-25px'>Dane zmienione (utrata integralności)</label>
                        </div>
                        <div className='row'>
                            <div className="col-lg-6">
                                <select className="form-select intensity" onChange={e => changeIntegrityLossRisk("value", e)} value={data.integrityLoss.value ? data.integrityLoss.value : "0"}>
                                    <option value="0" disabled>Wybierz odpowiedź</option>
                                    <option value="1">Wciąż jest możliwość skorygowania zmienionych danych</option>
                                    <option value="2">Nie ma możliwości skorygowania zmienionych danych</option>
                                    <option value="3">Nie dotyczy</option>
                                </select>
                            </div>
                            <div className='col-lg-6 mt-sm-2'>
                                <textarea value={data.integrityLoss.justification} onChange={e => changeIntegrityLossText("justification", e)} className="form-control" placeholder="Opis (opcja" />
                            </div>
                        </div>

                        <div className='row col-12 mt-2'>
                            <label className='text-medium-gray mb-25px'>Dane niedostępne (utrata dostępności)</label>
                        </div>
                        <div className='row'>
                            <div className="col-lg-6">
                                <select className="form-select intensity" onChange={e => changeAvailabilityLossRisk("value", e)} value={data.availabilityLoss.value}>
                                    <option value="0" disabled>Wybierz odpowiedź</option>
                                    <option value="1">Tymczasowa niedostępność danych</option>
                                    <option value="2">Stała niedostępność danych i nie ma możliwości ich odzyskania</option>
                                    <option value="3">Nie dotyczy</option>
                                </select>
                            </div>
                            <div className='col-lg-6 mt-sm-2'>
                                <textarea value={data.availabilityLoss.justification} onChange={e => changeAvailabilityLossText("justification", e)} className="form-control" placeholder="Opis (opcja)" />
                            </div>
                        </div>

                        <div className='row col-12 mt-2'>
                            <label className='text-medium-gray mb-25px'>Celowe działanie?</label>
                        </div>
                        <div className='row'>
                            <div className="col-lg-6">
                            <select className="form-select intensity" onChange={e => changeIntentionalActionRisk("value", e)} value={data.intentionalAction.value ? data.intentionalAction.value : "0"}>
                                <option value="0" disabled>Wybierz odpowiedź</option>
                                <option value="1">Naruszenie było celowe</option>
                                <option value="2">Naruszenie było przypadkowe</option>
                            </select>
                            </div>
                            <div className='col-lg-6 mt-sm-2'>
                            <textarea value={data.intentionalAction.justification} onChange={e => changeIntentionalActionText("justification", e)} className="form-control" placeholder="Opis (opcja)" />
                            </div>
                        </div>
                    </Panel>
                </div>
                <div className="row text-right">
                    <div className='calc-pdf-btn'>
                        <PDFDownloadLink className='btn btn-medium btn-dark-gray btn-box-shadow btn-round-edge text-transform-none primary-font submit' document={document()}>Zapisz do pliku PDF</PDFDownloadLink>
                    </div>
                </div>
            </div>
        </div>
        <div className='col-lg-2 mt-4 col-sm-12'>
            <div className='col'><h2 style={{visibility: "hidden"}}>A</h2></div>
            <div className="card bg-light mb-3" style={{paddingLeft: 0, paddingRight: 0, position: 'sticky', top: "10px"}}>
                <div className='card-header'><b>Waga naruszenia</b></div>
                <div className="card-body">
                    <div className='row'>
                        <div className='col-12'>
                            <div className='progress-bar-style-02'>
                                <div className='progress mb-15px border-radius-50px fw-700 fs-11 lh-11 text-white bg-white'>
                                    <div className="progress-bar bg-gradient-flamingo-red-transparent m-0 appear" role="progressbar" aria-valuenow={98} aria-valuemin={0} aria-valuemax={100} aria-label="business" style={{ "width": riskTotal.progressBarValue + "%" }} />
                                    <div className="progress-bar-title text-uppercase"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-12'>
                            <b>{riskTotal.name}</b>
                            <p>{riskTotal.details}</p>
                            <p><b>{riskTotal.reportObligation}</b></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
  );
}
